import { cn } from '@sunwebgroup/uds-ui/utils'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { useRouter } from 'next/router'
import * as React from 'react'
import { useIntl } from 'react-intl'
import { useCMSPageField } from '@/cms/fields'
import { groupBookingsAPIClient } from '@/infrastructure/groups/api'
import { Custom500 } from '@/pages/500'
import { BasketLegacy } from '@/scopes/checkout/components/Basket/BasketLegacy'
import { CheckoutError } from '@/scopes/checkout/components/CheckoutError/CheckoutError'
import { CheckoutHeader } from '@/scopes/checkout/components/CheckoutHeader'
import {
  CheckoutHeading,
  CheckoutHeadingDescription,
  CheckoutHeadingTitle,
} from '@/scopes/checkout/components/CheckoutHeading'
import { CheckoutProgress } from '@/scopes/checkout/components/CheckoutProgress'

import {
  Content,
  Loader,
  MainContent,
  PrimaryContent,
  SideBarContent,
} from '@/scopes/checkout/components/Layout'
import { useSendCheckoutEvent } from '@/scopes/checkout/domain/analytics/useSendCheckoutEvent'
import { CheckoutReactContext } from '@/scopes/checkout/domain/CheckoutContext'
import { useBookingDraftQuery, useCheckoutSid, useCheckoutSidSession } from '@/scopes/checkout/domain/hooks'
import { PageLayout } from '@/scopes/checkout/groups/components/PageLayout'
import { PersonalDetailsForm } from '@/scopes/checkout/groups/components/PersonalDetailsForm'
import { GroupsDependenciesProvider } from '@/scopes/checkout/groups/dependencies/context'
import { checkoutAPIClient } from '@/scopes/checkout/infrastructure/api'
import { CheckoutGroupsNotificationError } from '@/scopes/checkout/pages/groups/Components/NotificationError'
import { DevTools } from '@/scopes/common/components/DevTools/DevTools'
import { useConfiguration } from '@/scopes/common/infrastructure/config/ConfigProvider'
import { NewRelicErrorBoundary } from '@/scopes/common/infrastructure/logging/NewRelicErrorBoundary'
import createNextRouter from '@/scopes/common/infrastructure/router/nextRouter'
import styles from './pages.module.css'

const queryClient = new QueryClient()

function Page() {
  const intl = useIntl()
  const sid = useCheckoutSid()
  const { hasChecked } = useCheckoutSidSession()
  const { data } = useBookingDraftQuery()
  const brandName = useConfiguration('brandName')

  useSendCheckoutEvent()

  if (!hasChecked) {
    return (
      <PageLayout>
        <Loader />
      </PageLayout>
    )
  }

  if (!sid) {
    return (
      <PageLayout>
        <CheckoutGroupsNotificationError />
      </PageLayout>
    )
  }

  return (
    <PageLayout>
      {data?.error?.hasError && <CheckoutError showRedirectButton />}
      {(!data?.error || (data?.error && !data.error.hasError)) && (
        <NewRelicErrorBoundary scope="groups-page" fallback={<Custom500 brand={brandName} />}>
          <CheckoutHeader showLoginMenu={false} />
          <MainContent>
            <div className={cn(styles.flexContent, styles.flexContentGap12)}>
              <CheckoutProgress />
              <CheckoutHeading>
                <CheckoutHeadingTitle>
                  {intl.formatMessage({ id: 'groups_personal_data_step_title' })}
                </CheckoutHeadingTitle>
                <CheckoutHeadingDescription>
                  {intl.formatMessage({ id: 'groups_checkout_steps_extra_services_description' })}
                </CheckoutHeadingDescription>
              </CheckoutHeading>
              <Content>
                <PrimaryContent>
                  <PersonalDetailsForm />
                </PrimaryContent>
                <SideBarContent>
                  {/* Group checkout always uses BasketLegacy: the new Basket doesn't yet render groupInformation (name/size). */}
                  <BasketLegacy />
                </SideBarContent>
              </Content>
            </div>
          </MainContent>
        </NewRelicErrorBoundary>
      )}
    </PageLayout>
  )
}

export default function PersonalDetailsStep() {
  const contextItemId = useCMSPageField('contextItemId')
  const router = useRouter()
  const [sid, setSid] = React.useState<string | null>(null)
  const checkoutContextValue = React.useMemo(() => ({
    contextItemId,
    draftId: sid || null,
    setDraftId: setSid,
    step: 2,
  }), [contextItemId, sid])

  return (
    <QueryClientProvider client={queryClient}>
      <CheckoutReactContext value={checkoutContextValue}>
        <GroupsDependenciesProvider
          dependencies={{ checkoutAPIClient, groupBookingsAPIClient, router: createNextRouter(router) }}
        >
          <Page />
          <DevTools />
        </GroupsDependenciesProvider>
      </CheckoutReactContext>
    </QueryClientProvider>
  )
}
